import { HttpService, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { AggregatedEmailStatusCode, EmailDeliveryStatus, SaleDocumentKind } from '@skylab-shared/dataModel';
import { Mutable } from '@skylab-shared/types';
import { StringUtils } from '@skylab-shared/utils';
import { Transform, Type } from 'class-transformer';
import { ClientGroupCollectionQuery } from '../../client/queries/client-group.query-base';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { CurrencyCollectionQuery, CurrencyItem } from '../../currency/queries/currency.query-base';
import { PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO } from './../../common/query/paged-collection-query';

export type UnsettledDocumentsItem = Mutable<UnsettledDocumentsItemDTO> & {
    clientAddress: string | null;
    clientCountry: string;
    clientGroup: string;
    tin: string;

    currency: CurrencyItem;
    baseCurrencyItem: CurrencyItem;

    finalAdvance: boolean;
    advanceValue: number;

    kind: SaleDocumentKind,
    leftToPay: number;
};

class UnsettledDocumentsItemDTO {
    public readonly id!: number;
    public readonly amountToPay!: number;
    public readonly baseCurrency!: string;
    public readonly baseCurrencyAmountToPay!: number;
    public readonly baseCurrencyGross!: number;
    public readonly baseCurrencyNet!: number;
    public readonly baseCurrencyVat!: number;
    public readonly client!: {
        readonly id?: number;
        readonly removed: boolean;
        readonly address: {
            readonly city: string,
            readonly countryId: number,
            readonly line1: string,
            readonly name: string,
            readonly zipCode: string
        },
        readonly emails: string[],
        readonly phone?: string,
        readonly groupId: string,
        readonly name: string,
        readonly tin: string
    };
    public readonly currency!: string;
    public readonly delay!: number;
    public readonly documentId!: number;
    public readonly documentKind!: SaleDocumentKind;
    public readonly documentNumber!: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dueDate!: Date;
    public readonly gross!: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate!: Date;
    public readonly net!: number;
    public readonly proforma!: boolean;
    public readonly vat!: number;
    public readonly aggregatedEmailStatusCode!: AggregatedEmailStatusCode;
    public readonly finalAdvance!: boolean;
    public readonly advanceValue!: number;
    public readonly note: string | null;
    @Type(() => EmailDeliveryStatus) public readonly emailStatuses!: EmailDeliveryStatus[];
}

export class UnsettledDocumentsQuery extends PagedCollectionQuery<UnsettledDocumentsItem> { }

export class UnsettledDocumentsQueryHandlerBase implements QueryHandler<UnsettledDocumentsQuery, PagedCollectionQueryResult<UnsettledDocumentsItem>> {
    private static Url = TaggedUrl.join('sales/unsettledDocuments', [Tags.Api]);

    public constructor(
        private readonly http: HttpService,
        private readonly rootContext: RootContextBase
    ) { }

    public async handleAsync(query: UnsettledDocumentsQuery): Promise<PagedCollectionQueryResult<UnsettledDocumentsItem>> {
        const { data } = await this.http.get(UnsettledDocumentsQueryHandlerBase.Url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<UnsettledDocumentsItemDTO>(UnsettledDocumentsItemDTO)
        });

        const items = await Promise.all(data.items.map(async item => {
            const currency = await this.rootContext.firstAsync(CurrencyCollectionQuery, { symbol: item.currency });
            const baseCurrency = await this.rootContext.firstAsync(CurrencyCollectionQuery, { symbol: item.baseCurrency });
            const country = await this.rootContext.firstAsync(CountryCollectionQuery, { id: item.client?.address?.countryId });
            const clientGroup = await this.rootContext.firstAsync(ClientGroupCollectionQuery, { externalId: item.client?.groupId });

            return <UnsettledDocumentsItem>{
                ...item,
                clientAddress: item.client?.address ? StringUtils.join(', ', item.client.address.line1, item.client.address.city) : null,
                clientCountry: country?.name || '',
                clientGroup: clientGroup?.name || '',
                tin: item.client?.tin,
                currency: currency,
                baseCurrencyItem: baseCurrency,
                finalAdvance: item.finalAdvance,
                advanceValue: item.advanceValue,
                id: item.documentId,
                kind: item.documentKind,
                leftToPay: item.amountToPay,
            };
        }));

        return {
            items: items,
            totalItemsCount: data.totalItemsCount
        };
    }
}
